<template>
  <v-dialog v-model="dialog" width="90%" persistent scrollable>
    <v-card class="d-flex flex-column" style="max-height: 90vh;">
      <v-toolbar density="compact" color="primary" dark class="flex-shrink-0">
        <v-toolbar-title>
          {{ isEditing ? $t('projects.editProject') : $t('projects.createProject') }}
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn 
          v-if="isEditing && formData.idProject"
          icon
          @click="showHistory = true"
          :disabled="loading"
          :title="$t('projects.history.viewHistory')"
        >
          <v-icon>mdi-history</v-icon>
        </v-btn>
        <v-btn 
          v-if="isEditing && formData.idProject && !folderExists"
          icon
          @click="handleCreateFolder"
          :disabled="loading || checkingFolder || !localBackendStore.isConnected"
          :title="$t('projects.createFolder')"
        >
          <v-icon>mdi-folder-plus</v-icon>
        </v-btn>
        <v-btn 
          v-if="isEditing && formData.idProject && folderExists"
          icon
          @click="handleOpenFolder"
          :disabled="loading || !localBackendStore.isConnected"
          :title="$t('projects.openFolder')"
        >
          <v-icon>mdi-folder-open</v-icon>
        </v-btn>
        <v-btn 
          v-if="canShowArchive"
          icon
          @click="handleArchiveProject"
          :disabled="loading || archiving || !isUserAdmin"
          :title="isUserAdmin ? $t('projects.archiveProject') : $t('projects.archiveAdminOnly')"
          color="warning"
        >
          <v-icon>mdi-archive</v-icon>
        </v-btn>
        <v-btn icon @click="closeDialog" :disabled="loading">
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <v-btn 
          variant="text"
          @click="saveProject"
          :loading="loading"
          :disabled="!valid"
        >
          <v-icon left>mdi-content-save</v-icon>
          {{ $t('common.save') }}
        </v-btn>
      </v-toolbar>

      <v-card-text class="pt-4 overflow-y-auto flex-grow-1">
        <!-- Error Alert -->
        <v-alert v-if="errorMessage" type="error" dismissible @click:close="errorMessage = ''">
          {{ errorMessage }}
        </v-alert>

        <!-- Concurrency Conflict Alert -->
        <v-alert v-if="concurrencyConflict" type="warning" prominent border="start">
          <v-row align="center">
            <v-col class="grow">
              <div class="text-h6 mb-2">
                <v-icon start>mdi-alert</v-icon>
                {{ $t('projects.concurrency.title') }}
              </div>
              <div>{{ $t('projects.concurrency.message') }}</div>
            </v-col>
          </v-row>
          <v-divider class="my-4"></v-divider>
          <v-row>
            <v-col>
              <v-btn
                color="primary"
                variant="flat"
                @click="reloadAndRetry"
                prepend-icon="mdi-reload"
              >
                {{ $t('projects.concurrency.reload') }}
              </v-btn>
              <v-btn
                color="secondary"
                variant="text"
                @click="dismissConflict"
                class="ml-2"
              >
                {{ $t('projects.concurrency.cancel') }}
              </v-btn>
            </v-col>
          </v-row>
        </v-alert>

        <!-- Initial Status Selection (only for new projects) -->
        <v-container v-if="!isEditing && formData.projectType">
          <v-row>
            <v-col cols="12">
              <v-card outlined color="info">
                <v-card-title class="text-h6">
                  {{ $t('projects.selectInitialStatus') }}
                </v-card-title>
                <v-card-text>
                  <v-radio-group v-model="formData.activeStatus" inline>
                    <v-radio
                      v-for="status in initialStatusOptions"
                      :key="status.id"
                      :label="`${status.icon} ${status.descr}`"
                      :value="String(status.id)"
                      :color="status.color"
                    ></v-radio>
                  </v-radio-group>
                  <v-alert type="info" variant="tonal" density="compact" class="mt-2">
                    {{ $t('projects.initialStatusHint') }}
                  </v-alert>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-container>

        <!-- Status Section (only for editing) -->
        <v-container v-if="isEditing" class="pa-0 pb-2">
          <v-card outlined>
            <v-card-title class="text-subtitle-1 py-2 bg-grey-lighten-4">
              {{ $t('projects.fields.activeStatus') }}
            </v-card-title>
            <v-card-text class="pa-3">
              <v-row dense align="center">
                <!-- Current Status Info -->
                <v-col cols="12" md="4">
                  <div class="d-flex align-center">
                    <span class="text-h6 mr-2">{{ currentStatusInfo?.icon }}</span>
                    <div>
                      <div class="text-body-1 font-weight-medium">{{ currentStatusInfo?.descr }}</div>
                      <v-chip 
                        :color="currentStatusInfo?.color" 
                        label
                        size="x-small"
                        class="mt-1"
                      >
                        {{ $t('projects.status') }}: {{ parseInt(formData.activeStatus || 0) }}
                      </v-chip>
                    </div>
                  </div>
                </v-col>
                
                <!-- Follow-up Button -->
                <v-col cols="12" md="3" v-if="currentStatusInfo?.followUpDays">
                  <v-btn
                    color="success"
                    @click="executeFollowUp"
                    prepend-icon="mdi-calendar-check"
                    size="small"
                  >
                    {{ $t('projects.followUpExecuted') }}
                  </v-btn>
                </v-col>
                
                <!-- Next Status Buttons -->
                <v-col cols="12" :md="currentStatusInfo?.followUpDays ? 5 : 8">
                  <!-- Single next state button -->
                  <v-btn
                    v-if="canAdvanceStatus && nextStatesOptions.length === 1"
                    :color="hasAdvancedStatus ? 'warning' : 'primary'"
                    @click="advanceToStatus(nextStatesOptions[0].id)"
                    :prepend-icon="hasAdvancedStatus ? 'mdi-arrow-left-circle' : 'mdi-arrow-right-circle'"
                    size="small"
                  >
                    <span v-if="hasAdvancedStatus">{{ $t('common.cancelStatusChange') }}</span>
                    <span v-else>{{ nextStatesOptions[0].icon }} {{ nextStatesOptions[0].descr }}</span>
                  </v-btn>
                  
                  <!-- Multiple next states options -->
                  <div v-else-if="canAdvanceStatus && nextStatesOptions.length > 1">
                    <v-btn
                      v-if="hasAdvancedStatus"
                      color="warning"
                      @click="advanceToStatus(null)"
                      prepend-icon="mdi-arrow-left-circle"
                      size="small"
                      class="mb-2"
                    >
                      {{ $t('common.cancelStatusChange') }}
                    </v-btn>
                    <div v-else>
                      <div class="text-caption text-grey mb-1">{{ $t('projects.selectNextStatus') }}:</div>
                      <div class="d-flex flex-wrap gap-1">
                        <v-btn
                          v-for="nextState in nextStatesOptions"
                          :key="nextState.id"
                          color="primary"
                          variant="outlined"
                          @click="advanceToStatus(nextState.id)"
                          prepend-icon="mdi-arrow-right-circle"
                          size="small"
                        >
                          {{ nextState.icon }} {{ nextState.descr }}
                        </v-btn>
                      </div>
                    </div>
                  </div>
                  
                  <!-- Final status chip -->
                  <v-chip v-else-if="!canAdvanceStatus && !currentStatusInfo?.followUpDays" color="success" size="small">
                    <template v-slot:prepend>
                      <v-icon size="small">mdi-check-circle</v-icon>
                    </template>
                    {{ $t('projects.finalStatus') }}
                  </v-chip>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-container>

        <!-- Status Instructions - Collapsible -->
        <v-container v-if="isEditing && currentStatusInfo?.instructions" class="pa-0 pb-2">
          <v-expansion-panels>
            <v-expansion-panel>
              <v-expansion-panel-title 
                :style="{ backgroundColor: currentStatusInfo.color + '20' }"
                class="py-1"
                density="compact"
              >
                <div class="d-flex align-center">
                  <v-icon size="small" class="mr-2" :color="currentStatusInfo.color">mdi-information-outline</v-icon>
                  <span class="text-body-2 font-weight-medium">Instruções para {{ currentStatusInfo.descr }}</span>
                </div>
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <div class="status-instructions pt-3" v-html="currentStatusInfo.instructions"></div>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-container>

        <v-container class="pa-0">
          <v-form ref="form" v-model="valid" lazy-validation>
            <!-- Loop through groups -->
            <v-card 
              v-for="(group, groupName) in fieldsByGroup" 
              :key="groupName" 
              elevation="2"
              class="mb-2"
            >
              <v-card-title :class="`bg-${group.color}-lighten-4 text-body-1 py-1`">
                {{ group.description }}
              </v-card-title>
              <v-card-text class="pt-2 pb-2">
                <!-- Fields in this group -->
                <v-row dense>
                  <v-col 
                    v-for="(field, fieldName) in group.fields" 
                    :key="fieldName"
                    :cols="getFieldColumns(field.type)"
                    :md="getFieldColumnsMd(field.type)"
                  >
                <!-- Text Field -->
                <v-text-field
                  v-if="field.type === 'text'"
                  v-model="formData[fieldName]"
                  :label="field.label || getFieldLabel(fieldName)"
                  :readonly="isFieldLocked(field)"
                  :rules="getFieldRules(fieldName, field)"
                  :class="{ 'readonly-field': isFieldLocked(field) }"
                  :bg-color="isFieldLocked(field) ? 'grey-lighten-4' : undefined"
                  outlined
                  dense
                  density="compact"
                >
                  <template v-if="isFieldLocked(field)" v-slot:prepend-inner>
                    <v-icon size="small" color="grey">mdi-lock</v-icon>
                  </template>
                </v-text-field>

                <!-- Number Field -->
                <v-text-field
                  v-else-if="field.type === 'number'"
                  v-model="formData[fieldName]"
                  :label="field.label || getFieldLabel(fieldName)"
                  :readonly="isFieldLocked(field)"
                  :rules="getFieldRules(fieldName, field)"
                  :class="{ 'readonly-field': isFieldLocked(field) }"
                  :bg-color="isFieldLocked(field) ? 'grey-lighten-4' : undefined"
                  type="number"
                  outlined
                  dense
                  density="compact"
                >
                  <template v-if="isFieldLocked(field)" v-slot:prepend-inner>
                    <v-icon size="small" color="grey">mdi-lock</v-icon>
                  </template>
                </v-text-field>

                <!-- Currency Field -->
                <v-text-field
                  v-else-if="field.type === 'currency'"
                  v-model="formData[fieldName]"
                  @focus="(e) => handleCurrencyFocus(fieldName, e)"
                  @blur="(e) => handleCurrencyBlur(fieldName, e)"
                  :label="field.label || getFieldLabel(fieldName)"
                  :readonly="isFieldLocked(field)"
                  :rules="getFieldRules(fieldName, field)"
                  :class="{ 'readonly-field': isFieldLocked(field) }"
                  :bg-color="isFieldLocked(field) ? 'grey-lighten-4' : undefined"
                  outlined
                  dense
                  density="compact"
                >
                  <template v-if="isFieldLocked(field)" v-slot:prepend-inner>
                    <v-icon size="small" color="grey">mdi-lock</v-icon>
                  </template>
                </v-text-field>

                <!-- Select Field for projectType -->
                <v-select
                  v-else-if="field.type === 'select' && fieldName === 'projectType'"
                  v-model="formData[fieldName]"
                  :items="projectTypes"
                  item-title="descr"
                  item-value="id"
                  :label="field.label || getFieldLabel(fieldName)"
                  :readonly="isFieldLocked(field)"
                  :rules="getFieldRules(fieldName, field)"
                  :class="{ 'readonly-field': isFieldLocked(field) }"
                  :bg-color="isFieldLocked(field) ? 'grey-lighten-4' : undefined"
                  outlined
                  dense
                  density="compact"
                >
                  <template v-if="isFieldLocked(field)" v-slot:prepend-inner>
                    <v-icon size="small" color="grey">mdi-lock</v-icon>
                  </template>
                ></v-select>

                <!-- Select Field for activeStatus -->
                <v-select
                  v-else-if="field.type === 'select' && fieldName === 'activeStatus'"
                  v-model="formData[fieldName]"
                  :items="statusList"
                  item-title="descr"
                  item-value="id"
                  :label="field.label || getFieldLabel(fieldName)"
                  :readonly="isFieldLocked(field) || isEditing"
                  :rules="getFieldRules(fieldName, field)"
                  :class="{ 'readonly-field': isFieldLocked(field) || isEditing }"
                  :bg-color="(isFieldLocked(field) || isEditing) ? 'grey-lighten-4' : undefined"
                  outlined
                  dense
                  density="compact"
                >
                  <template v-if="isFieldLocked(field) || isEditing" v-slot:prepend-inner>
                    <v-icon size="small" color="grey">mdi-lock</v-icon>
                  </template>
                ></v-select>

                <!-- Select Field generic (uses options from field config) -->
                <v-select
                  v-else-if="field.type === 'select' && field.options"
                  v-model="formData[fieldName]"
                  :items="field.options"
                  item-title="text"
                  item-value="value"
                  :label="field.label || getFieldLabel(fieldName)"
                  :readonly="isFieldLocked(field)"
                  :rules="getFieldRules(fieldName, field)"
                  :class="{ 'readonly-field': isFieldLocked(field) }"
                  :bg-color="isFieldLocked(field) ? 'grey-lighten-4' : undefined"
                  outlined
                  dense
                  density="compact"
                >
                  <template v-if="isFieldLocked(field)" v-slot:prepend-inner>
                    <v-icon size="small" color="grey">mdi-lock</v-icon>
                  </template>
                ></v-select>

                <!-- Multi-Select Field (uses options from field config) -->
                <v-select
                  v-else-if="field.type === 'multiselect' && field.options"
                  v-model="formData[fieldName]"
                  :items="field.options"
                  item-title="text"
                  item-value="value"
                  :label="field.label || getFieldLabel(fieldName)"
                  :readonly="isFieldLocked(field)"
                  :rules="getFieldRules(fieldName, field)"
                  :class="{ 'readonly-field': isFieldLocked(field) }"
                  :bg-color="isFieldLocked(field) ? 'grey-lighten-4' : undefined"
                  multiple
                  chips
                  outlined
                  dense
                  density="compact"
                >
                  <template v-if="isFieldLocked(field)" v-slot:prepend-inner>
                    <v-icon size="small" color="grey">mdi-lock</v-icon>
                  </template>
                ></v-select>

                <!-- Date Field -->
                <v-text-field
                  v-else-if="field.type === 'date'"
                  v-model="formData[fieldName]"
                  :label="field.label || getFieldLabel(fieldName)"
                  :readonly="isFieldLocked(field)"
                  :rules="getFieldRules(fieldName, field)"
                  :class="{ 'readonly-field': isFieldLocked(field) }"
                  :bg-color="isFieldLocked(field) ? 'grey-lighten-4' : undefined"
                  type="date"
                  outlined
                  dense
                  density="compact"
                >
                  <template v-if="isFieldLocked(field)" v-slot:prepend-inner>
                    <v-icon size="small" color="grey">mdi-lock</v-icon>
                  </template>
                ></v-text-field>

                <!-- Textarea -->
                <v-textarea
                  v-else-if="field.type === 'textarea'"
                  v-model="formData[fieldName]"
                  :label="field.label || getFieldLabel(fieldName)"
                  :readonly="isFieldLocked(field)"
                  :rules="getFieldRules(fieldName, field)"
                  :class="{ 'readonly-field': isFieldLocked(field) }"
                  :bg-color="isFieldLocked(field) ? 'grey-lighten-4' : undefined"
                  outlined
                  rows="3"
                  density="compact"
                >
                  <template v-if="isFieldLocked(field)" v-slot:prepend-inner>
                    <v-icon size="small" color="grey">mdi-lock</v-icon>
                  </template>
                ></v-textarea>

                <!-- Checkbox -->
                <v-checkbox
                  v-else-if="field.type === 'checkbox'"
                  v-model="formData[fieldName]"
                  :label="field.label || getFieldLabel(fieldName)"
                  :readonly="isFieldLocked(field)"
                  :rules="getFieldRules(fieldName, field)"
                  :class="{ 'readonly-field': isFieldLocked(field) }"
                  color="primary"
                  hide-details="auto"
                  density="compact"
                >
                  <template v-if="isFieldLocked(field)" v-slot:prepend>
                    <v-icon size="small" color="grey">mdi-lock</v-icon>
                  </template>
                </v-checkbox>

                <!-- Notes List -->
                <NotesField
                  v-else-if="field.type === 'notelist'"
                  v-model="formData[fieldName]"
                  :label="field.label || getFieldLabel(fieldName)"
                  :readonly="isFieldLocked(field) || hasAdvancedStatus"
                  :currentStatus="formData.activeStatus || '0'"
                  :projectType="formData.projectType || '0'"
                />
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
          </v-form>
        </v-container>
      </v-card-text>
    </v-card>

    <!-- Project History Dialog -->
    <ProjectHistory 
      v-model="showHistory" 
      :projectId="formData.idProject"
      :currentProject="formData"
      @restored="handleFieldsRestored"
    />

    <!-- Archive Confirmation Dialog -->
    <ConfirmDialog
      v-model="showArchiveConfirm"
      :title="$t('projects.archiveConfirmTitle')"
      :message="$t('projects.archiveConfirmMessage')"
      :confirmText="$t('projects.archiveConfirm')"
      :cancelText="$t('common.cancel')"
      confirmColor="warning"
      :loading="archiving"
      @confirm="confirmArchive"
      @cancel="showArchiveConfirm = false"
    />
  </v-dialog>
</template>

<script setup>
import { ref, computed, onMounted, nextTick } from 'vue'
import { useProjectStore } from '../stores/projects.js'
import { useAuthStore } from '../stores/auth.js'
import { useLocalBackendStore } from '../stores/localBackend.js'
import { useI18n } from 'vue-i18n'
import ProjectHistory from './ProjectHistory.vue'
import ConfirmDialog from './ConfirmDialog.vue'
import NotesField from './NotesField.vue'
import { addBusinessDays } from '../utils/dateUtils.js'

const { t } = useI18n()
const localBackendStore = useLocalBackendStore()

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  project: {
    type: Object,
    default: null
  },
  isEditing: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue', 'projectSaved'])

const projectStore = useProjectStore()
const authStore = useAuthStore()
const form = ref()
const valid = ref(false)
const loading = ref(false)
const errorMessage = ref('')
const hasAttemptedSubmit = ref(false)
const hasAdvancedStatus = ref(false)
const originalStatus = ref(null)
const folderExists = ref(false)
const checkingFolder = ref(false)
const showHistory = ref(false)
const concurrencyConflict = ref(false)
const pendingChanges = ref(null)
const showArchiveConfirm = ref(false)
const archiving = ref(false)

const dialog = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
})

const isEditing = computed(() => props.isEditing)
const currentStatus = computed(() => {
  const status = formData.value.activeStatus
  if (isEditing.value && status) {
    return parseInt(status)
  }
  return 0 // Status inicial para novos projetos
})

// Lista di stati basata sul tipo di progetto selezionato
const statusList = computed(() => {
  const projectType = formData.value.projectType
  const projectTypeId = projectType ? parseInt(projectType) : null
  if (projectTypeId === null) {
    return projectStore.statusList.map(status => ({
      ...status,
      id: String(status.id)
    }))
  }
  const statuses = projectStore.getStatusListForType(projectTypeId)
  return statuses.map(status => ({
    ...status,
    id: String(status.id)
  }))
})

const projectTypes = computed(() => {
  return projectStore.projectTypes.map(type => ({
    ...type,
    id: String(type.id)
  }))
})

const transportTypes = ['Marítimo', 'Aéreo', 'Rodoviário', 'Ferroviário']

// Get initial status options (optional states + first mandatory state)
const initialStatusOptions = computed(() => {
  const projectType = formData.value.projectType
  if (!projectType) return []
  
  const projectTypeId = parseInt(projectType)
  const typeStatuses = projectStore.getStatusListForType(projectTypeId)
  
  // Return only states that can be selected as initial
  // These are states marked as isOptional: true
  // If no optional states, return the first state in the list
  const optionalStates = typeStatuses.filter(s => s.isOptional === true)
  
  if (optionalStates.length > 0) {
    return optionalStates
  }
  
  // No optional states, return just the first one
  return typeStatuses.length > 0 ? [typeStatuses[0]] : []
})

// Status management
const currentStatusInfo = computed(() => {
  // Usa lo stato ORIGINALE del progetto, non quello modificato nel form
  const status = props.project?.activeStatus || formData.value.activeStatus
  const projectType = formData.value.projectType
  if (!status || !projectType) return null
  const statusId = parseInt(status)
  const projectTypeId = parseInt(projectType)
  return projectStore.getStatusInfo(String(statusId), projectTypeId)
})

const nextStatusInfo = computed(() => {
  const status = formData.value.activeStatus
  const projectType = formData.value.projectType
  if (!status || !projectType) return null
  const projectTypeId = parseInt(projectType)
  const currentStatusId = parseInt(status)
  
  // Get current status info
  const currentStatus = projectStore.getStatusInfo(String(currentStatusId), projectTypeId)
  if (!currentStatus) return null
  
  // Check if nextStates is defined
  if (currentStatus.nextStates && currentStatus.nextStates.length > 0) {
    // Get first next state from nextStates array
    const nextStatusId = currentStatus.nextStates[0]
    return projectStore.getStatusInfo(String(nextStatusId), projectTypeId)
  }
  
  // Fallback: sequential next state
  const typeStatuses = projectStore.getStatusListForType(projectTypeId)
  const currentIndex = typeStatuses.findIndex(s => s.id === currentStatusId)
  
  if (currentIndex === -1 || currentIndex >= typeStatuses.length - 1) return null
  
  return typeStatuses[currentIndex + 1]
})

const canAdvanceStatus = computed(() => {
  const status = formData.value.activeStatus
  const projectType = formData.value.projectType
  if (!status || !projectType) return false
  const projectTypeId = parseInt(projectType)
  const currentStatusId = parseInt(status)
  
  // Get current status info
  const currentStatus = projectStore.getStatusInfo(String(currentStatusId), projectTypeId)
  if (!currentStatus) return false
  
  // Check if nextStates is defined
  if (currentStatus.nextStates !== undefined) {
    return currentStatus.nextStates.length > 0
  }
  
  // Fallback: check if there's a next state in the list
  const typeStatuses = projectStore.getStatusListForType(projectTypeId)
  const currentIndex = typeStatuses.findIndex(s => s.id === currentStatusId)
  
  return currentIndex !== -1 && currentIndex < typeStatuses.length - 1
})

const isUserAdmin = computed(() => {
  const userRole = authStore.user?.role
  return userRole === 'admin' || userRole === 'superadmin'
})

const canShowArchive = computed(() => {
  if (!isEditing.value) return false
  
  const status = formData.value.activeStatus
  const projectType = formData.value.projectType
  if (!status || !projectType) return false
  
  const projectTypeId = parseInt(projectType)
  const currentStatusId = parseInt(status)
  
  // Get current status info
  const currentStatus = projectStore.getStatusInfo(String(currentStatusId), projectTypeId)
  if (!currentStatus) return false
  
  // Check if current status has canArchive flag
  return currentStatus.canArchive === true
})

// Get all possible next states as array of status objects
const nextStatesOptions = computed(() => {
  const status = formData.value.activeStatus
  const projectType = formData.value.projectType
  if (!status || !projectType) return []
  const projectTypeId = parseInt(projectType)
  const currentStatusId = parseInt(status)
  
  // Get current status info
  const currentStatus = projectStore.getStatusInfo(String(currentStatusId), projectTypeId)
  if (!currentStatus) return []
  
  // Check if nextStates is defined
  if (currentStatus.nextStates && currentStatus.nextStates.length > 0) {
    // Map nextStates IDs to full status objects
    return currentStatus.nextStates
      .map(id => projectStore.getStatusInfo(String(id), projectTypeId))
      .filter(s => s !== null)
  }
  
  // Fallback: sequential next state
  const typeStatuses = projectStore.getStatusListForType(projectTypeId)
  const currentIndex = typeStatuses.findIndex(s => s.id === currentStatusId)
  
  if (currentIndex === -1 || currentIndex >= typeStatuses.length - 1) return []
  
  return [typeStatuses[currentIndex + 1]]
})

const advanceToStatus = async (statusId) => {
  if (!hasAdvancedStatus.value) {
    // Save original status before advancing (only first time, keep it for subsequent cycles)
    if (originalStatus.value === null || originalStatus.value === formData.value.activeStatus) {
      originalStatus.value = formData.value.activeStatus
    }
    
    // Change status first
    formData.value.activeStatus = String(statusId)
    hasAdvancedStatus.value = true
    
    // Wait for Vue to update
    await nextTick()
    
    // Validate the form (this will show errors if any fields are missing)
    if (form.value && typeof form.value.validate === 'function') {
      hasAttemptedSubmit.value = true
      const { valid } = await form.value.validate()
      
      if (!valid) {
        errorMessage.value = 'Por favor, preencha todos os campos obrigatórios antes de salvar.'
      } else {
        errorMessage.value = ''
      }
    }
    
    // Automatically set reminderDate based on new status followUpDays
    const projectType = formData.value.projectType
    if (projectType) {
      const projectTypeId = parseInt(projectType)
      const statusInfo = projectStore.getStatusInfo(String(statusId), projectTypeId)
      
      if (statusInfo && statusInfo.followUpDays) {
        // Calculate new date: today + followUpDays
        const today = new Date()
        today.setDate(today.getDate() + statusInfo.followUpDays)
        
        // Format date as YYYY-MM-DD for date field
        const newReminderDate = today.toISOString().split('T')[0]
        
        formData.value.reminderDate = newReminderDate
      } else {
        // If no followUpDays, clear the reminder date
        formData.value.reminderDate = null
      }
    }
  } else {
    // If already advanced, revert to original status
    formData.value.activeStatus = originalStatus.value
    hasAdvancedStatus.value = false
    // DON'T reset originalStatus to null - keep it for subsequent cycles
    errorMessage.value = ''
    
    // Wait for Vue to update and revalidate
    await nextTick()
    
    // Revalidate the form with the original status
    if (form.value && typeof form.value.validate === 'function') {
      await form.value.validate()
    }
  }
}

const executeFollowUp = () => {
  const projectType = formData.value.projectType
  const status = formData.value.activeStatus
  
  if (!projectType || !status) return
  
  const projectTypeId = parseInt(projectType)
  const currentStatusId = parseInt(status)
  
  // Ottieni le informazioni dello status corrente
  const statusInfo = projectStore.getStatusInfo(String(currentStatusId), projectTypeId)
  
  if (statusInfo && statusInfo.followUpDays) {
    // Calcola la nuova data: oggi + followUpDays (giorni utili)
    const today = new Date()
    const newDate = addBusinessDays(today, statusInfo.followUpDays)
    
    // Formatta la data in formato YYYY-MM-DD per il campo date
    const newReminderDate = newDate.toISOString().split('T')[0]
    
    formData.value.reminderDate = newReminderDate
  }
}

// Campi visibili basati su fields.json e status corrente
const visibleFields = computed(() => {
  const fields = {}
  const fieldsConfig = projectStore.fields || []
  const currentProjectType = formData.value.projectType
  
  // If fields is an array (new structure)
  if (Array.isArray(fieldsConfig)) {
    for (const field of fieldsConfig) {
      // Skip status field (shown separately in edit mode)
      if (field.name === 'activeStatus' || field.name === 'status') {
        continue
      }
      
      // Always show projectType field
      if (field.name === 'projectType') {
        fields[field.name] = field
        continue
      }
      
      // For NEW projects: only show projectType until it's selected
      if (!isEditing.value && 
          (currentProjectType === undefined || 
           currentProjectType === null || 
           currentProjectType === '')) {
        // Don't show other fields yet
        continue
      }
      
      // For other fields, check if visible in current project type
      if (field.visibleInProjectTypes) {
        if (currentProjectType !== undefined && 
            currentProjectType !== null && 
            currentProjectType !== '') {
          if (!field.visibleInProjectTypes.includes(parseInt(currentProjectType))) {
            continue
          }
        }
      }
      
      // Check conditional visibility
      if (field.conditionalOn) {
        const conditionField = field.conditionalOn.field
        const conditionValue = field.conditionalOn.value
        const currentValue = formData.value[conditionField]
        
        // For multiselect fields, check if the value is in the array
        if (Array.isArray(currentValue)) {
          if (!currentValue.includes(conditionValue)) {
            continue
          }
        } else {
          // For single value fields, check direct equality
          if (currentValue !== conditionValue) {
            continue
          }
        }
      }
      
      fields[field.name] = field
    }
  } else {
    // Fallback for old object structure
    for (const [fieldName, fieldConfig] of Object.entries(fieldsConfig)) {
      // Skip system fields
      if (['id', 'createdAt', 'createdBy', 'lastModifiedAt', 'lastModifiedBy'].includes(fieldName)) {
        continue
      }
      
      // Skip activeStatus if not editing
      if (fieldName === 'activeStatus' && !isEditing.value) {
        continue
      }
      
      // Check visibility
      if (projectStore.isFieldVisible(fieldName, currentStatus.value)) {
        fields[fieldName] = fieldConfig
      }
    }
  }
  
  return fields
})

// Group fields by their group property
const fieldsByGroup = computed(() => {
  const groups = {}
  const fields = visibleFields.value
  
  for (const [fieldName, field] of Object.entries(fields)) {
    if (field.group) {
      const groupKey = field.group.description
      if (!groups[groupKey]) {
        groups[groupKey] = {
          description: field.group.description,
          color: field.group.color,
          fields: {}
        }
      }
      groups[groupKey].fields[fieldName] = field
    } else {
      // Fields without group go to "Outros"
      if (!groups['Outros']) {
        groups['Outros'] = {
          description: 'Outros',
          color: 'grey',
          fields: {}
        }
      }
      groups['Outros'].fields[fieldName] = field
    }
  }
  
  return groups
})

// Dados do formulário (dinamici)
const formData = ref({})
const originalFieldValues = ref({}) // Track original values when loading

// Initialize form data with all possible fields from fields.json
const initializeFormData = () => {
  const data = {}
  const fieldsConfig = projectStore.fields || []
  
  // If fields is an array (new structure)
  if (Array.isArray(fieldsConfig)) {
    for (const field of fieldsConfig) {
      if (field.name === 'activeStatus' || field.name === 'status') {
        data.activeStatus = '0'
      } else if (field.name === 'projectType') {
        data.projectType = ''
      } else if (field.type === 'date') {
        data[field.name] = ''
      } else if (field.type === 'number') {
        data[field.name] = ''
      } else if (field.type === 'multiselect' || field.type === 'notelist') {
        data[field.name] = []
      } else {
        data[field.name] = ''
      }
    }
  } else {
    // Fallback for old object structure
    for (const fieldName of Object.keys(fieldsConfig)) {
      if (fieldName === 'activeStatus') {
        data[fieldName] = '0'
      } else if (fieldsConfig[fieldName].type === 'date') {
        data[fieldName] = ''
      } else if (fieldsConfig[fieldName].type === 'multiselect' || fieldsConfig[fieldName].type === 'notelist') {
        data[fieldName] = []
      } else {
        data[fieldName] = ''
      }
    }
  }
  
  return data
}

// Helper functions for field rendering
const getFieldLabel = (fieldName) => {
  return t(`projects.fields.${fieldName}`)
}

// Handle currency field focus - remove formatting for editing
const handleCurrencyFocus = (fieldName, event) => {
  const value = formData.value[fieldName]
  if (!value) return
  
  // If already formatted (contains dots), remove formatting
  const strValue = value.toString()
  if (strValue.includes('.') || strValue.includes(',')) {
    // Remove all dots (thousand separators) and replace comma with dot
    const cleanValue = strValue.replace(/\./g, '').replace(',', '.')
    formData.value[fieldName] = cleanValue
  }
  
  // Select all text for easy replacement
  setTimeout(() => {
    event.target.select()
  }, 0)
}

// Handle currency field blur - apply formatting
const handleCurrencyBlur = (fieldName, event) => {
  let value = formData.value[fieldName]
  if (!value) return
  
  // Normalize the input: handle both comma and dot as decimal separator
  let strValue = value.toString().trim()
  
  // Count dots and commas to understand the format
  const dotCount = (strValue.match(/\./g) || []).length
  const commaCount = (strValue.match(/,/g) || []).length
  
  // If has both dots and comma, assume Italian format (dots=thousands, comma=decimal)
  if (dotCount > 0 && commaCount > 0) {
    strValue = strValue.replace(/\./g, '').replace(',', '.')
  }
  // If has multiple dots, assume thousand separator (e.g., 1.234.567)
  else if (dotCount > 1) {
    strValue = strValue.replace(/\./g, '')
  }
  // If has only comma, assume decimal separator
  else if (commaCount === 1) {
    strValue = strValue.replace(',', '.')
  }
  // If has one dot, it's already correct (decimal separator)
  
  // Parse the number
  const numValue = parseFloat(strValue)
  
  if (isNaN(numValue)) {
    formData.value[fieldName] = ''
    return
  }
  
  // Format as Italian currency: 1.234.567,89
  const formatted = new Intl.NumberFormat('it-IT', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(numValue)
  
  formData.value[fieldName] = formatted
}

const getFieldColumns = (fieldType) => {
  // Full width fields for mobile
  return 12
}

const getFieldColumnsMd = (fieldType) => {
  // Full width fields on medium+ screens
  if (fieldType === 'textarea' || fieldType === 'notelist') {
    return 12
  }
  // 3 columns layout: 12 / 3 = 4
  return 3
}

const isFieldRequired = (fieldName) => {
  return projectStore.isFieldRequired(fieldName, currentStatus.value)
}

// Check if field should be locked (readonly)
const isFieldLocked = (field) => {
  // Always respect the readonly property
  if (field.readonly) {
    return true
  }
  
  // If lockAfterCreation is true and we're editing
  if (field.lockAfterCreation && isEditing.value) {
    // Check if this field had a value when the form was loaded
    const originalValue = originalFieldValues.value[field.name]
    
    // For multiselect and notelist fields, check if original array had values
    if (field.type === 'multiselect' || field.type === 'notelist') {
      return Array.isArray(originalValue) && originalValue.length > 0
    }
    
    // For other fields, lock if there was an original value
    return originalValue !== undefined && originalValue !== null && originalValue !== ''
  }
  
  return false
}

// Regras de validação dinâmicas
const getFieldRules = (fieldName, fieldConfig) => {
  // Don't show validation errors until user tries to submit
  if (!hasAttemptedSubmit.value) {
    return []
  }
  
  const rules = []
  let required = false
  
  // Check if new structure with validationByStatus
  if (fieldConfig.validationByStatus) {
    const currentStatusStr = String(currentStatus.value)
    const currentStatusNum = parseInt(currentStatus.value)
    
    // Check if required in current status
    let validation = fieldConfig.validationByStatus[currentStatusStr] || fieldConfig.validationByStatus['*']
    required = validation?.required || false
    
    // If not required in current status, check all previous statuses
    // A field required in status 9 should remain required in 10, 11, etc.
    if (!required && !isNaN(currentStatusNum)) {
      for (const statusKey in fieldConfig.validationByStatus) {
        if (statusKey !== '*') {
          const statusNum = parseInt(statusKey)
          if (!isNaN(statusNum) && statusNum <= currentStatusNum) {
            const prevValidation = fieldConfig.validationByStatus[statusKey]
            if (prevValidation?.required) {
              required = true
              break
            }
          }
        }
      }
    }
  } else {
    // Fallback to old structure
    required = projectStore.isFieldRequired(fieldName, currentStatus.value)
  }
  
  if (required) {
    // For multiselect and notelist fields, check if array has at least one element
    if (fieldConfig.type === 'multiselect' || fieldConfig.type === 'notelist') {
      rules.push(v => (Array.isArray(v) && v.length > 0) || t('projects.validation.fieldRequired', { field: getFieldLabel(fieldName) }))
    } else {
      // For other fields, check if value is not empty
      rules.push(v => !!v || t('projects.validation.fieldRequired', { field: getFieldLabel(fieldName) }))
    }
  }
  
  // Regole specifiche per campo
  if (fieldConfig.type === 'date') {
    rules.push(v => {
      if (!v) return true
      const date = new Date(v)
      return !isNaN(date.getTime()) || t('projects.validation.invalidDate')
    })
  }
  
  // Validazione caratteri filesystem per il campo cliente
  if (fieldName === 'Client' || fieldName === 'client' || fieldName === 'cliente') {
    rules.push(v => {
      if (!v) return true
      const invalidChars = /[<>:"/\\|?*]/
      return !invalidChars.test(v) || t('projects.validation.invalidFilesystemChars')
    })
  }
  
  return rules
}

// Carregar dados quando o componente é montado
onMounted(async () => {
  if (props.project) {
    // Editing mode - load project data
    const data = initializeFormData()
    const fieldsConfig = projectStore.fields || []
    
    // Create a map of field names (both PascalCase and camelCase) to field configs
    const fieldMap = {}
    if (Array.isArray(fieldsConfig)) {
      for (const field of fieldsConfig) {
        fieldMap[field.name] = field
        // Also map both camelCase and PascalCase versions
        const camelCase = field.name.charAt(0).toLowerCase() + field.name.slice(1)
        const pascalCase = field.name.charAt(0).toUpperCase() + field.name.slice(1)
        fieldMap[camelCase] = field
        fieldMap[pascalCase] = field
      }
    }
    
    // Copy all project properties to formData
    for (const [key, value] of Object.entries(props.project)) {
      const fieldConfig = fieldMap[key]
      
      if (fieldConfig) {
        const fieldName = fieldConfig.name
        
        if (fieldName === 'projectType' || fieldName === 'activeStatus') {
          data[fieldName] = String(value ?? '')
        } else if (fieldConfig.type === 'date' && value) {
          data[fieldName] = new Date(value).toISOString().split('T')[0]
        } else {
          data[fieldName] = value || ''
        }
      }
    }
    
    // Copy system fields that are not in fields.json
    if (props.project.idProject) data.idProject = props.project.idProject
    if (props.project.createdAt) data.createdAt = props.project.createdAt
    if (props.project.createdBy) data.createdBy = props.project.createdBy
    if (props.project.updatedAt) data.updatedAt = props.project.updatedAt
    if (props.project.updatedBy) data.updatedBy = props.project.updatedBy
    if (props.project.version !== undefined) data.version = props.project.version
    
    formData.value = data
    
    // Store original values for lockAfterCreation logic
    originalFieldValues.value = { ...data }
    
    // Store original status for cancel functionality
    originalStatus.value = data.activeStatus
    
    // Check if folder exists
    const projectId = props.project.idProject
    if (projectId) {
      await checkFolderExists()
    }
  } else {
    // Create mode - initialize empty form
    formData.value = initializeFormData()
  }
})

const checkFolderExists = async () => {
  const projectId = formData.value.idProject
  if (!projectId) return
  
  try {
    checkingFolder.value = true
    folderExists.value = await projectStore.checkProjectFolder(projectId)
  } catch (error) {
    console.error('Error checking folder:', error)
    folderExists.value = false
  } finally {
    checkingFolder.value = false
  }
}

const resetForm = () => {
  formData.value = initializeFormData()
  form.value?.resetValidation()
  errorMessage.value = ''
  hasAttemptedSubmit.value = false
  hasAdvancedStatus.value = false
  originalStatus.value = null
  concurrencyConflict.value = false
  pendingChanges.value = null
}

const closeDialog = () => {
  dialog.value = false
  resetForm()
}

const dismissConflict = () => {
  concurrencyConflict.value = false
  pendingChanges.value = null
  closeDialog()
}

const reloadAndRetry = async () => {
  try {
    loading.value = true
    concurrencyConflict.value = false
    
    // Reload the current version of the project
    const freshProject = await projectStore.getProject(props.project.idProject)
    
    if (!freshProject) {
      errorMessage.value = t('projects.concurrency.reloadError')
      return
    }
    
    // Show user a message that data has been reloaded
    errorMessage.value = t('projects.concurrency.reloadSuccess')
    
    // Update form with fresh data
    const data = initializeFormData()
    const fieldsConfig = projectStore.fields || []
    const fieldMap = {}
    
    if (Array.isArray(fieldsConfig)) {
      for (const field of fieldsConfig) {
        fieldMap[field.name] = field
        const camelCase = field.name.charAt(0).toLowerCase() + field.name.slice(1)
        const pascalCase = field.name.charAt(0).toUpperCase() + field.name.slice(1)
        fieldMap[camelCase] = field
        fieldMap[pascalCase] = field
      }
    }
    
    for (const [key, value] of Object.entries(freshProject)) {
      const fieldConfig = fieldMap[key]
      
      if (fieldConfig) {
        const fieldName = fieldConfig.name
        
        if (fieldName === 'projectType' || fieldName === 'activeStatus') {
          data[fieldName] = String(value ?? '')
        } else if (fieldConfig.type === 'date' && value) {
          data[fieldName] = new Date(value).toISOString().split('T')[0]
        } else {
          data[fieldName] = value || ''
        }
      }
    }
    
    // Copy system fields
    if (freshProject.idProject) data.idProject = freshProject.idProject
    if (freshProject.createdAt) data.createdAt = freshProject.createdAt
    if (freshProject.createdBy) data.createdBy = freshProject.createdBy
    if (freshProject.updatedAt) data.updatedAt = freshProject.updatedAt
    if (freshProject.updatedBy) data.updatedBy = freshProject.updatedBy
    if (freshProject.version !== undefined) data.version = freshProject.version
    
    formData.value = data
    pendingChanges.value = null
    
  } catch (error) {
    console.error('Error reloading project:', error)
    errorMessage.value = t('projects.concurrency.updateError')
  } finally {
    loading.value = false
  }
}

const handleCreateFolder = async () => {
  const projectId = formData.value.idProject
  const client = formData.value.client
  if (!projectId) return
  
  try {
    loading.value = true
    await projectStore.createProjectFolder(projectId, client)
    folderExists.value = true
  } catch (error) {
    console.error('Error creating folder:', error)
    errorMessage.value = t('projects.folderError') + ': ' + (error.message || 'Unknown error')
  } finally {
    loading.value = false
  }
}

const handleOpenFolder = async () => {
  const projectId = formData.value.idProject
  if (!projectId) return
  
  try {
    loading.value = true
    await projectStore.openProjectFolder(projectId)
    // Success feedback could be added here via snackbar/toast
    console.log('Folder opened successfully')
  } catch (error) {
    console.error('Error opening folder:', error)
    errorMessage.value = t('projects.folderError') + ': ' + (error.message || 'Unknown error')
  } finally {
    loading.value = false
  }
}

const handleArchiveProject = () => {
  showArchiveConfirm.value = true
}

const confirmArchive = async () => {
  const projectId = formData.value.idProject
  if (!projectId) return
  
  try {
    archiving.value = true
    await projectStore.archiveProject(projectId)
    showArchiveConfirm.value = false
    // Close the dialog and emit success
    dialog.value = false
    emit('archived', projectId)
  } catch (error) {
    console.error('Error archiving project:', error)
    errorMessage.value = t('projects.archiveError') + ': ' + (error.message || 'Unknown error')
  } finally {
    archiving.value = false
  }
}

const handleFieldsRestored = async (event) => {
  // Reload the project to get the updated values
  try {
    loading.value = true
    const updatedProject = await projectStore.getProject(formData.value.idProject)
    
    if (updatedProject) {
      // Update form data with the restored values
      const data = initializeFormData()
      const fieldsConfig = projectStore.fields || []
      const fieldMap = {}
      
      if (Array.isArray(fieldsConfig)) {
        for (const field of fieldsConfig) {
          fieldMap[field.name] = field
          const camelCase = field.name.charAt(0).toLowerCase() + field.name.slice(1)
          const pascalCase = field.name.charAt(0).toUpperCase() + field.name.slice(1)
          fieldMap[camelCase] = field
          fieldMap[pascalCase] = field
        }
      }
      
      for (const [key, value] of Object.entries(updatedProject)) {
        const fieldConfig = fieldMap[key]
        
        if (fieldConfig) {
          const fieldName = fieldConfig.name
          
          if (fieldName === 'projectType' || fieldName === 'activeStatus') {
            data[fieldName] = String(value ?? '')
          } else if (fieldConfig.type === 'date' && value) {
            data[fieldName] = new Date(value).toISOString().split('T')[0]
          } else {
            data[fieldName] = value || ''
          }
        }
      }
      
      // Copy system fields
      if (updatedProject.idProject) data.idProject = updatedProject.idProject
      if (updatedProject.createdAt) data.createdAt = updatedProject.createdAt
      if (updatedProject.createdBy) data.createdBy = updatedProject.createdBy
      if (updatedProject.updatedAt) data.updatedAt = updatedProject.updatedAt
      if (updatedProject.updatedBy) data.updatedBy = updatedProject.updatedBy
      if (updatedProject.version !== undefined) data.version = updatedProject.version
      
      formData.value = data
      errorMessage.value = t('projects.history.restoreSuccess') || 'Campos restaurados com sucesso!'
    }
  } catch (error) {
    console.error('Error reloading project after restore:', error)
    errorMessage.value = t('projects.history.restoreError')
  } finally {
    loading.value = false
  }
}

const saveProject = async () => {
  hasAttemptedSubmit.value = true
  
  // Validate form first
  if (form.value && typeof form.value.validate === 'function') {
    const { valid } = await form.value.validate()
    if (!valid) {
      errorMessage.value = 'Por favor, preencha todos os campos obrigatórios.'
      return
    }
  }
  
  // Additional validation for required fields
  const projectType = formData.value.projectType
  if (!projectType || projectType === '') {
    errorMessage.value = 'Por favor, selecione o tipo de projeto.'
    return
  }

  try {
    loading.value = true
    errorMessage.value = ''
    
    // For new projects, set reminderDate based on initial status if not already set
    if (!isEditing.value && formData.value.activeStatus && !formData.value.reminderDate) {
      const projectTypeId = parseInt(formData.value.projectType)
      const statusId = parseInt(formData.value.activeStatus)
      const statusInfo = projectStore.getStatusInfo(String(statusId), projectTypeId)
      
      if (statusInfo && statusInfo.followUpDays) {
        const today = new Date()
        today.setDate(today.getDate() + statusInfo.followUpDays)
        formData.value.reminderDate = today.toISOString().split('T')[0]
      }
    }
    
    // Preparar dados para envio
    const projectData = {}
    const fieldsConfig = projectStore.fields || []
    
    // If fields is an array (new structure)
    if (Array.isArray(fieldsConfig)) {
      for (const field of fieldsConfig) {
        const fieldName = field.name
        const value = formData.value[fieldName]
        
        // Handle activeStatus field
        if (field.name === 'activeStatus' || field.name === 'status') {
          projectData.activeStatus = String(formData.value.activeStatus || '0')
          continue
        }
        
        // Convert based on field type
        if (field.type === 'date') {
          projectData[fieldName] = value ? new Date(value).toISOString() : null
        } else {
          projectData[fieldName] = value || ''
        }
      }
    } else {
      // Fallback for old object structure
      for (const [fieldName, value] of Object.entries(formData.value)) {
        const fieldConfig = fieldsConfig[fieldName]
        if (!fieldConfig) continue
        
        // Convert based on field type
        if (fieldName === 'projectType' || fieldName === 'activeStatus') {
          projectData[fieldName] = String(value)
        } else if (fieldConfig.type === 'date') {
          projectData[fieldName] = value ? new Date(value).toISOString() : null
        } else {
          projectData[fieldName] = value || ''
        }
      }
    }

    let result
    if (isEditing.value) {
      // Include version for concurrency check
      if (formData.value.version !== undefined) {
        projectData.version = formData.value.version
      }
      
      try {
        result = await projectStore.updateProject(props.project.idProject, projectData)
      } catch (updateError) {
        if (updateError.isConcurrencyConflict) {
          // Handle concurrency conflict
          concurrencyConflict.value = true
          pendingChanges.value = projectData
          loading.value = false
          hasAdvancedStatus.value = false
          return
        }
        throw updateError
      }
    } else {
      result = await projectStore.createProject(projectData)
      
      // Update formData with the generated idProject
      if (result && result.idProject) {
        formData.value.idProject = result.idProject
        
        // Automatically create the project folder
        try {
          await projectStore.createProjectFolder(result.idProject, result.client)
          folderExists.value = true
        } catch (folderError) {
          console.error('Error creating folder:', folderError)
          // Don't fail the whole operation if folder creation fails
        }
      }
    }

    emit('projectSaved', result)
    closeDialog()
  } catch (error) {
    console.error('Erro ao salvar projeto:', error)
    errorMessage.value = error.message || 'Erro ao salvar o projeto. Verifique os campos obrigatórios.'
  } finally {
    loading.value = false
    hasAdvancedStatus.value = false
  }
}
</script>

<style scoped>
.headline {
  font-weight: 500;
}

.readonly-field {
  opacity: 0.85;
}

.readonly-field :deep(.v-field) {
  cursor: not-allowed;
}

.readonly-field :deep(.v-field__input) {
  color: rgba(0, 0, 0, 0.6);
}

.status-instructions {
  line-height: 1.7;
}

.status-instructions :deep(p) {
  margin-bottom: 0.5rem;
}

.status-instructions :deep(ol) {
  margin-left: 0;
  padding-left: 1.5rem;
}

.status-instructions :deep(ul) {
  margin-left: 0;
  padding-left: 1.5rem;
  margin-top: 0.25rem;
  margin-bottom: 0.25rem;
}

.status-instructions :deep(li) {
  margin-bottom: 0.5rem;
}

.status-instructions :deep(strong) {
  color: #1976d2;
  font-weight: 600;
}

.status-instructions :deep(em) {
  color: #666;
  font-style: italic;
}

.status-instructions :deep(.text-success) {
  color: #4caf50;
}

.status-instructions :deep(.tip) {
  background-color: #fff3cd;
  border-left: 4px solid #ffc107;
  padding: 0.75rem;
  margin-top: 1rem;
  border-radius: 4px;
  font-size: 0.9rem;
}
</style>